/**
 * Tenant dashboard · Referrals.
 *
 * Three screens behind one tab, because on a phone they are three screens and
 * on a desktop they are one page:
 *
 *   hub    — the code, the reward rules, progress toward the free month
 *   rooms  — what is actually available to refer someone TO, linked to the
 *            public listing so the friend sees the real page
 *   mine   — every referral this tenant has made, and where each one stands
 *
 * Sharing the bare code is CODE-ONLY: there is no coliviq.de/r/<code> page, so
 * the programme never offers one. Sharing a ROOM is different — that link is
 * the room's application page with the code in its query string (roomApplyUrl),
 * so the friend lands on the form with the referral field already filled in.
 *
 * "Available rooms" means free today OR with an admin-confirmed move-out date
 * (rooms.available_from is only ever written from one) — see
 * getReferralAvailability. A room whose tenant has merely not given notice is
 * not listed: there is nothing to promise a friend.
 *
 * Everything the tenant sees about the people they referred comes from the
 * my_referrals() RPC, which masks names to "Sara T." and returns no contact
 * details at all. The referrals table's own RLS deliberately does not let a
 * referrer read the referred person's booking.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useScrollToTopOnChange } from "@/hooks/useScrollToTopOnChange";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Check,
  ChevronDown,
  ChevronRight,
  Copy,
  ExternalLink,
  Gift,
  Loader2,
  Mail,
  MessageCircle,
  MessageSquare,
  Share2,
  TriangleAlert,
  Upload,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/lib/supabase";
import {
  formatNextAvailableDate,
  getReferralAvailability,
} from "@/lib/roomAvailability";
import { resolveRoomPhoto } from "@/lib/v2-images";
import { getImageUrl, getImageSrcSet } from "@/lib/imageUrl";
import roomPlaceholder from "@/assets/room-1.jpg";
import {
  EMPTY_REFERRALS,
  REFERRAL_REWARD_CENTS,
  REFERRAL_STAGES,
  buildShareMessage,
  formatEuroFromCents,
  referralInitials,
  referralProgress,
  referralStepFourBody,
  rentMonthLabel,
  roomApplyUrl,
  roomListingUrl,
  stageHint,
  stageIndex,
  stageLabel,
  referralsBehindMilestone,
  type MyReferrals,
  type ReferralBonus,
  type ReferralItem,
  type ReferralStage,
} from "@/lib/referrals";

type Screen = "hub" | "rooms" | "mine";

/** The thumbnail box is 80px at its largest; ask the CDN for 2x and 3x too. */
/**
 * Last-resort room photo, for the rooms that have neither their own image nor a
 * property one (9 of 97 on dev). This is the SAME bundled asset the public
 * listing falls back to in Properties.tsx — it is a stock interior, not a photo
 * of that room, but matching the listing is the point: what a referrer sees
 * here is what their friend sees when they open the link.
 */
const FALLBACK_ROOM_PHOTO = roomPlaceholder;

const THUMB_WIDTH = 160;
const THUMB_SRCSET = [80, 160, 240];

interface ReferrableRoom {
  id: number;
  title: string;
  price: number;
  propertyTitle: string;
  city: string | null;
  /** Cover photo for the listing. Never null — see FALLBACK_ROOM_PHOTO. */
  photo: string;
  /** Confirmed move-out date when the room is not free today; null when it is. */
  availableFrom: string | null;
}

/** What the share sheet is currently sharing: the bare code, or a room with it. */
type ShareContext = { room: { label: string; url: string } | null } | null;

export default function ReferralsPanel() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const queryClient = useQueryClient();
  const de = language === "de";

  const [screen, setScreen] = useState<Screen>("hub");
  // Each sub-screen opens at the top of the page, like a new page would.
  useScrollToTopOnChange(screen);
  const [howOpen, setHowOpen] = useState(true);
  const [copied, setCopied] = useState(false);
  const [share, setShare] = useState<ShareContext>(null);
  const [message, setMessage] = useState("");

  // ── the tenant's own code ───────────────────────────────────────────────
  const {
    data: referralCode,
    isLoading: codeLoading,
    isError: codeFailed,
  } = useQuery<string | null>({
    queryKey: ["referral-code", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from("profiles")
        .select("referral_code")
        .eq("id", user.id)
        .maybeSingle();
      if (error) throw error;
      return (
        (data as { referral_code?: string | null } | null)?.referral_code ??
        null
      );
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });

  // ── the referral list + the totals the hero renders ─────────────────────
  // Totals come from the same RPC as the list so the headline number can never
  // disagree with the rows underneath it.
  //
  // isError is not optional here. The default is EMPTY_REFERRALS, and an empty
  // MyReferrals renders as "0 successful · €0 earned" — which is exactly what a
  // genuine new referrer sees. Without this flag a failed RPC would tell a
  // tenant with nine successful referrals that they have none, in the calm
  // voice of a correct answer.
  const {
    data: referrals = EMPTY_REFERRALS,
    isLoading: listLoading,
    isError: listFailed,
  } = useQuery<MyReferrals>({
    queryKey: ["my-referrals", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("my_referrals");
      if (error) throw new Error(error.message);
      // jsonb: the MyReferrals shape, or null.
      return (data as unknown as MyReferrals | null) ?? EMPTY_REFERRALS;
    },
    enabled: !!user?.id,
  });

  // Stages advance from admin actions in another session entirely (an approval,
  // a signed contract landing via the DocuSign webhook), so this view has to be
  // pushed, not polled. Mirrors the bookings/payments channels in Dashboard.tsx.
  useEffect(() => {
    if (!user?.id) return undefined;
    const channel = supabase
      .channel(`dashboard-referrals-${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "referrals",
          filter: `referrer_user_id=eq.${user.id}`,
        },
        () => {
          queryClient.invalidateQueries({
            queryKey: ["my-referrals", user.id],
          });
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, queryClient]);

  // ── rooms worth referring someone to ────────────────────────────────────
  const { data: rooms = [], isLoading: roomsLoading } = useQuery<
    ReferrableRoom[]
  >({
    queryKey: ["referrable-rooms"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("rooms")
        .select(
          `id, title, price, available, available_from, next_available_date, is_hidden, images,
           properties ( title, city, is_hidden, image_url )`
        )
        .eq("is_hidden", false)
        .order("price", { ascending: true });
      if (error) throw error;

      type Row = {
        id: number;
        title: string | null;
        price: number | null;
        available: boolean | null;
        available_from: string | null;
        next_available_date: string | null;
        images: unknown;
        properties: {
          title?: string | null;
          city?: string | null;
          is_hidden?: boolean;
          image_url?: string | null;
        } | null;
      };

      return (
        ((data ?? []) as unknown as Row[])
          // A room in a hidden property is not on the public site, so referring
          // someone to it would send them to a page they cannot open.
          .filter((r) => r.properties && r.properties.is_hidden !== true)
          // Free today, or with an admin-confirmed move-out date — the two
          // things a friend can actually plan around. An occupied room with no
          // confirmed date is left out (COL-434).
          .map((r) => ({
            row: r,
            availability: getReferralAvailability({
              available: r.available,
              available_from: r.available_from,
              next_available_date: r.next_available_date,
            }),
          }))
          .filter(({ availability }) => availability.referrable)
          .map(({ row: r, availability }) => ({
            id: r.id,
            availableFrom: availability.availableFrom,
            title: (r.title ?? "").trim() || (de ? "Zimmer" : "Room"),
            price: Number(r.price ?? 0),
            propertyTitle: (r.properties?.title ?? "").trim(),
            city: r.properties?.city ?? null,
            // Third fallback matches the public listing exactly (Properties.tsx
            // ends on the same bundled asset), so the card previews what the
            // friend will actually land on rather than a different placeholder.
            photo:
              resolveRoomPhoto(r.images, r.properties?.image_url) ??
              FALLBACK_ROOM_PHOTO,
          }))
      );
    },
    staleTime: 60 * 1000,
  });

  // ── which month a new credit would land on ───────────────────────────────
  // Mirrors the row apply_referral_reward picks: the tenant's earliest rent row
  // that is unpaid, not yet due, not already handed to Stripe, and large enough
  // to absorb a whole credit (a discounted or prorated month under EUR 100 is
  // skipped by the SQL too). Read here only to NAME the month in "How it
  // works"; the database decides where the money goes, and if nothing matches
  // the copy says "next" rather than guessing.
  const { data: nextRentDue = null } = useQuery<string | null>({
    queryKey: ["referral-next-rent-due", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const today = new Date();
      const iso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
      const { data, error } = await supabase
        .from("payments")
        .select("due_date")
        .eq("user_id", user.id)
        .eq("type", "rent")
        .in("status", ["pending", "scheduled"])
        .is("paid_at", null)
        .is("stripe_payment_intent_id", null)
        .gte("amount_cents", REFERRAL_REWARD_CENTS)
        .gte("due_date", iso)
        .order("due_date", { ascending: true })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return (data as { due_date?: string | null } | null)?.due_date ?? null;
    },
    enabled: !!user?.id,
    staleTime: 60 * 1000,
  });
  const nextRentMonth = rentMonthLabel(nextRentDue, de ? "de" : "en");

  // Progress within the CURRENT cycle, not against the lifetime total — past
  // ten the bar would otherwise sit permanently full and stop meaning anything.
  const progress = referralProgress(referrals.toward_next, referrals.goal);
  const code = referralCode ?? "";

  const openShare = useCallback(
    (ctx: ShareContext) => {
      // Defence in depth: every share control is disabled without a code, so
      // reaching here means something got past that. Say so rather than
      // no-oping — a button that swallows the click reads as a broken app.
      if (!code) {
        toast.error(
          de
            ? "Dein Empfehlungscode ist noch nicht verfügbar."
            : "Your referral code isn't available yet."
        );
        return;
      }
      setShare(ctx);
      setMessage(
        buildShareMessage({
          code,
          language: de ? "de" : "en",
          room: ctx?.room ?? null,
        })
      );
    },
    [code, de]
  );

  const copyCode = useCallback(async () => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      // Clipboard is blocked on insecure origins and in some in-app browsers.
      // The code is on screen either way — say what happened, don't fail silently.
      toast.error(
        de
          ? "Kopieren nicht möglich – Code bitte manuell abtippen."
          : "Couldn't copy — please type the code manually."
      );
    }
  }, [code, de]);

  if (codeLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {codeFailed && <CodeUnavailableNotice de={de} />}
      {listFailed && <ReferralsUnavailableNotice de={de} />}

      {screen === "hub" && (
        <HubScreen
          de={de}
          code={code}
          copied={copied}
          onCopy={copyCode}
          onShare={() => openShare({ room: null })}
          successful={referrals.successful}
          inProgress={referrals.in_progress}
          earnedCents={referrals.earned_cents}
          freeMonths={referrals.free_months}
          freeMonthPending={referrals.free_month_pending}
          rewardStalledCount={referrals.reward_stalled_count}
          bonuses={referrals.bonuses}
          items={referrals.items}
          progress={progress}
          howOpen={howOpen}
          onToggleHow={() => setHowOpen((v) => !v)}
          roomCount={rooms.length}
          referralCount={referrals.items.length}
          nextRentMonth={nextRentMonth}
          onGoRooms={() => setScreen("rooms")}
          onGoMine={() => setScreen("mine")}
        />
      )}

      {screen === "rooms" && (
        <RoomsScreen
          de={de}
          loading={roomsLoading}
          rooms={rooms}
          canShare={!!code}
          onBack={() => setScreen("hub")}
          onShareRoom={(room) =>
            openShare({
              room: {
                label: [room.title, room.propertyTitle]
                  .filter(Boolean)
                  .join(" · "),
                // The application page with the code in the URL — not the
                // listing — so the friend's referral field is pre-filled.
                url: roomApplyUrl(room.id, code),
              },
            })
          }
        />
      )}

      {screen === "mine" && (
        <MineScreen
          de={de}
          loading={listLoading}
          items={referrals.items}
          onBack={() => setScreen("hub")}
          onShare={() => openShare({ room: null })}
        />
      )}

      {share && code && (
        <ShareSheet
          de={de}
          code={code}
          message={message}
          onMessage={setMessage}
          roomLabel={share.room?.label ?? null}
          onClose={() => setShare(null)}
        />
      )}
    </div>
  );
}

/* ══════════════════════════ hub ══════════════════════════ */

function HubScreen(props: {
  de: boolean;
  code: string;
  copied: boolean;
  onCopy: () => void;
  onShare: () => void;
  successful: number;
  inProgress: number;
  earnedCents: number;
  freeMonths: number;
  freeMonthPending: boolean;
  rewardStalledCount: number;
  bonuses: ReferralBonus[];
  items: ReferralItem[];
  progress: ReturnType<typeof referralProgress>;
  howOpen: boolean;
  onToggleHow: () => void;
  roomCount: number;
  referralCount: number;
  /** "October" — the month a fresh credit would land on; null when unknown. */
  nextRentMonth: string | null;
  onGoRooms: () => void;
  onGoMine: () => void;
}) {
  const {
    de,
    code,
    copied,
    onCopy,
    onShare,
    successful,
    inProgress,
    earnedCents,
    freeMonths,
    freeMonthPending,
    rewardStalledCount,
    bonuses,
    items,
    progress,
    howOpen,
    onToggleHow,
    roomCount,
    referralCount,
    nextRentMonth,
    onGoRooms,
    onGoMine,
  } = props;

  const steps = de
    ? [
        [
          "Teile deinen Code",
          `Sende ${code || "deinen Code"} an Freunde – per WhatsApp, E-Mail oder SMS.`,
        ],
        [
          "Bewerbung mit deinem Code",
          "Deine Freundin oder dein Freund gibt den Code bei der Zimmerbewerbung ein.",
        ],
        [
          "Vertrag wird unterschrieben",
          "Sobald coliviq die Bewerbung genehmigt und der Vertrag unterschrieben ist, zählt die Empfehlung.",
        ],
        [
          "Du bekommst einmalig 100 € Rabatt",
          referralStepFourBody(nextRentMonth, "de"),
        ],
      ]
    : [
        [
          "Share your code",
          `Send ${code || "your code"} to a friend via WhatsApp, email or SMS.`,
        ],
        [
          "They apply with your code",
          "Your friend enters the code in the referral field when applying for a room.",
        ],
        [
          "They sign their contract",
          "Once coliviq approves the application and the lease is signed, the referral counts.",
        ],
        [
          "You get a one-time €100 off",
          referralStepFourBody(nextRentMonth, "en"),
        ],
      ];

  return (
    <>
      {/* ── the code ── */}
      <section className="relative overflow-hidden rounded-3xl p-6 sm:p-8 text-white shadow-lg bg-gradient-petrol">
        <div
          aria-hidden
          className="pointer-events-none absolute -right-16 -top-24 h-72 w-72 rounded-full"
          style={{
            background:
              "radial-gradient(circle at 30% 30%, hsl(var(--turkis) / 0.3), transparent 68%)",
          }}
        />
        <div className="relative">
          <p className="text-[10.5px] font-bold uppercase tracking-[0.15em] text-muted-on-dark">
            {de ? "Dein Empfehlungscode" : "Your referral code"}
          </p>

          <div className="mt-2 flex flex-wrap items-center gap-3">
            <span className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight tabular-nums">
              {code || "—"}
            </span>
            <button
              type="button"
              onClick={onCopy}
              disabled={!code}
              aria-label={de ? "Code kopieren" : "Copy code"}
              className="grid h-9 w-9 place-items-center rounded-xl border border-white/15 bg-white/10 transition hover:bg-white/20 disabled:opacity-40"
            >
              {copied ? (
                <Check className="h-4 w-4" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </button>
            {copied && (
              <span className="text-xs font-bold text-turkis">
                {de ? "Kopiert!" : "Copied!"}
              </span>
            )}
          </div>

          <p className="mt-3 max-w-[52ch] text-sm leading-relaxed text-white/85">
            {de
              ? "Wird deine Freundin oder dein Freund über deinen Code Mieter:in bei coliviq, bekommst du 100 € Rabatt auf deine nächste Miete."
              : "When a friend becomes a coliviq tenant using your code, you get €100 off your next rent."}
          </p>

          <button
            type="button"
            onClick={onShare}
            disabled={!code}
            className="mt-5 inline-flex items-center gap-2 rounded-full bg-turkis px-6 py-3 text-sm font-bold text-petrol-darker transition hover:brightness-110 disabled:opacity-40"
          >
            {de ? "Jetzt teilen" : "Share now"}
            <Share2 className="h-4 w-4" />
          </button>

          <div className="mt-6 max-w-lg border-t border-white/15 pt-4">
            <div className="flex items-baseline justify-between gap-3">
              <p className="font-display text-2xl font-extrabold tabular-nums">
                {successful}
                <span className="ml-1 text-sm font-semibold text-muted-on-dark">
                  / {progress.goal}
                </span>
              </p>
              <p className="text-xs text-muted-on-dark">
                {de ? "Erfolgreiche Empfehlungen" : "Successful referrals"}
              </p>
            </div>

            {/* Ten discrete pips, not a continuous bar: the goal IS a count of
                ten, so showing ten things a tenant can point at reads truer
                than a percentage they have to translate back. */}
            <div
              className="mt-3 grid gap-1"
              style={{
                gridTemplateColumns: `repeat(${progress.goal}, minmax(0, 1fr))`,
              }}
              role="img"
              aria-label={
                de
                  ? `${successful} von ${progress.goal} erfolgreichen Empfehlungen`
                  : `${successful} of ${progress.goal} successful referrals`
              }
            >
              {Array.from({ length: progress.goal }, (_, i) => (
                <span
                  key={i}
                  className={`h-1.5 rounded-full ${
                    i < progress.capped ? "bg-turkis" : "bg-white/15"
                  }`}
                />
              ))}
            </div>

            <p className="mt-2 text-[11.5px] text-muted-on-dark">
              {/* One free month per tenant, so once it is earned this says so
                  and stops counting. Anything like "N more to the next one"
                  would promise a second month that is not coming. */}
              {freeMonths > 0
                ? de
                  ? "Ein Monat Miete geschenkt – freigeschaltet."
                  : "One month of rent, on us — unlocked."
                : de
                  ? `Noch ${progress.goal - progress.capped}: dann ist 1 Monat Miete geschenkt.`
                  : `${progress.goal - progress.capped} more and one month of rent is on us.`}
            </p>
          </div>
        </div>
      </section>

      {/* ── the free month, when there is one to celebrate ── */}
      {(freeMonths > 0 || freeMonthPending) && (
        <FreeMonthCard
          de={de}
          bonuses={bonuses}
          items={items}
          pending={freeMonthPending}
        />
      )}

      {/* ── the three numbers a referrer tracks ── */}
      <div className="grid grid-cols-3 gap-3">
        <StatTile
          value={String(successful)}
          label={de ? "Erfolgreich" : "Successful"}
        />
        <StatTile
          value={String(inProgress)}
          label={de ? "In Bearbeitung" : "In progress"}
        />
        <StatTile
          value={formatEuroFromCents(earnedCents, de ? "de" : "en")}
          label={de ? "Bisher verdient" : "Earned so far"}
          accent
        />
      </div>

      {/* "Successful: 1" next to "Earned so far: EUR 0" is a contradiction from
          where the tenant sits. It happens when the credit has nowhere to land
          — almost always a referrer who has moved out and will not be billed
          again. Name it rather than leaving them to work it out. */}
      {/* Reads as actionable, not as a footnote: this is the one referral state
          the tenant has to do something about, and it is what the Referrals
          tab's pip points at (COL-368). A muted grey paragraph was easy to
          scroll past, which made the pip lead nowhere the eye could find. */}
      {rewardStalledCount > 0 && (
        <p
          role="status"
          data-testid="referral-reward-stalled-notice"
          className="rounded-2xl border border-amber-500/35 bg-amber-500/[0.12] p-3.5 text-[12px] leading-relaxed text-amber-950/95 shadow-sm dark:text-amber-100/95"
        >
          {de
            ? rewardStalledCount === 1
              ? "Eine Prämie ist verdient, kann aber gerade nicht verrechnet werden – dafür braucht es eine offene Miete. Melde dich bei uns, dann finden wir einen Weg."
              : `${rewardStalledCount} Prämien sind verdient, können aber gerade nicht verrechnet werden – dafür braucht es eine offene Miete. Melde dich bei uns, dann finden wir einen Weg.`
            : rewardStalledCount === 1
              ? "One reward is earned but cannot be credited right now — that needs an upcoming rent to apply it to. Get in touch and we will sort it out."
              : `${rewardStalledCount} rewards are earned but cannot be credited right now — that needs an upcoming rent to apply them to. Get in touch and we will sort it out.`}
        </p>
      )}

      <div className="grid gap-3 lg:grid-cols-2 lg:items-start">
        {/* ── how it works ── */}
        <div className="rounded-2xl border border-border bg-card shadow-sm">
          <button
            type="button"
            onClick={onToggleHow}
            aria-expanded={howOpen}
            className="flex w-full items-center justify-between gap-3 p-4 text-left"
          >
            <span className="font-display text-[15px] font-bold tracking-tight">
              {de ? "So funktioniert's" : "How it works"}
            </span>
            <ChevronDown
              className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${
                howOpen ? "rotate-180" : ""
              }`}
            />
          </button>
          {howOpen && (
            <ol className="flex flex-col gap-4 px-4 pb-4">
              {steps.map(([title, body], i) => (
                <li key={title} className="flex gap-3">
                  <span className="mt-0.5 grid h-[22px] w-[22px] shrink-0 place-items-center rounded-full bg-turkis-fill text-[11px] font-bold text-petrol">
                    {i + 1}
                  </span>
                  <div>
                    <p className="text-[13.5px] font-bold">{title}</p>
                    <p className="mt-0.5 text-[12.5px] leading-relaxed text-muted-foreground">
                      {body}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>

        {/* ── the two sub-screens ── */}
        <div className="flex flex-col gap-3">
          <NavRow
            count={roomCount}
            title={de ? "Verfügbare Zimmer" : "Available rooms"}
            subtitle={
              de
                ? "Zimmer, die du gerade empfehlen kannst"
                : "Rooms you can refer a friend to right now"
            }
            onClick={onGoRooms}
          />
          <NavRow
            count={referralCount}
            title={de ? "Meine Empfehlungen" : "My referrals"}
            subtitle={
              de
                ? "Verfolge jede Empfehlung bis zur Prämie"
                : "Track every referral from application to reward"
            }
            onClick={onGoMine}
          />
        </div>
      </div>
    </>
  );
}

/**
 * The free month, and the people who earned it.
 *
 * Named, not just counted. "You earned a free month" is a number; "Jonas, Sara
 * and eight others got you a free month" is the thing a tenant screenshots and
 * sends back to the friends who helped — which is the whole point of a referral
 * programme.
 *
 * Two states, and the difference matters to someone reading their statement:
 * EARNED means the month is won but has no full-price rent to land on yet (the
 * EUR 100 credits are still working through the upcoming months), APPLIED names
 * the month that was actually waived.
 */
function FreeMonthCard({
  de,
  bonuses,
  items,
  pending,
}: {
  de: boolean;
  bonuses: ReferralBonus[];
  items: ReferralItem[];
  pending: boolean;
}) {
  const applied = bonuses.filter((b) => b.applied_at);
  const waiting = bonuses.find((b) => !b.applied_at);
  const latest = applied[applied.length - 1] ?? waiting ?? null;
  if (!latest) return null;

  const people = referralsBehindMilestone(items, latest.milestone);
  const lang = de ? "de" : "en";

  const monthLabel = latest.waived_due_date
    ? new Date(latest.waived_due_date).toLocaleDateString(
        de ? "de-DE" : "en-GB",
        { month: "long", year: "numeric" }
      )
    : null;

  return (
    <section className="relative overflow-hidden rounded-3xl border border-turkis-strong bg-turkis-fill-soft p-5 sm:p-6">
      <div
        aria-hidden
        className="pointer-events-none absolute -right-10 -top-16 h-56 w-56 rounded-full"
        style={{
          background:
            "radial-gradient(circle at 40% 40%, hsl(var(--turkis) / 0.28), transparent 70%)",
        }}
      />

      <div className="relative flex flex-wrap items-start gap-4">
        <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-turkis text-petrol-darker">
          <Gift className="h-6 w-6" />
        </span>

        <div className="min-w-0 flex-1">
          <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-turkis-strong">
            {de
              ? `${latest.milestone} erfolgreiche Empfehlungen`
              : `${latest.milestone} successful referrals`}
          </p>
          <h3 className="mt-1 font-display text-xl font-extrabold tracking-tight text-petrol dark:text-turkis">
            {de ? "Ein Monat Miete geschenkt" : "One month of rent, on us"}
          </h3>

          <p className="mt-1.5 text-[13px] leading-relaxed text-foreground/75">
            {latest.applied_at && monthLabel ? (
              de ? (
                <>
                  Deine Miete für <strong>{monthLabel}</strong> wurde erlassen
                  {latest.amount_cents
                    ? ` — ${formatEuroFromCents(latest.amount_cents, lang)}.`
                    : "."}{" "}
                  Du musst nichts tun.
                </>
              ) : (
                <>
                  Your <strong>{monthLabel}</strong> rent has been waived
                  {latest.amount_cents
                    ? ` — ${formatEuroFromCents(latest.amount_cents, lang)}.`
                    : "."}{" "}
                  Nothing for you to do.
                </>
              )
            ) : de ? (
              "Verdient. Wir verrechnen ihn mit deiner nächsten vollen Monatsmiete — die 100-€-Gutschriften laufen zuerst durch."
            ) : (
              "Earned. It comes off your next full month's rent — your €100 credits work through the months before it."
            )}
          </p>

          {!latest.applied_at && (
            <span className="mt-2.5 inline-flex items-center gap-1.5 rounded-full border border-turkis-strong bg-card px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-turkis-strong">
              {de ? "Wird verrechnet" : "Being applied"}
            </span>
          )}

          {people.length > 0 && (
            <div className="mt-4 border-t border-turkis-strong/25 pt-3.5">
              <p className="text-[11px] font-semibold text-foreground/60">
                {de
                  ? `Dank dieser ${people.length}:`
                  : `Thanks to these ${people.length}:`}
              </p>
              <ul className="mt-2.5 flex flex-wrap gap-1.5">
                {people.map((person) => (
                  <li
                    key={person.id}
                    className="inline-flex items-center gap-1.5 rounded-full border border-turkis-strong/40 bg-card py-1 pl-1 pr-2.5"
                  >
                    <span className="grid h-6 w-6 place-items-center rounded-full bg-turkis-fill text-[10px] font-bold text-petrol">
                      {referralInitials(person.referred_name)}
                    </span>
                    <span className="text-[12px] font-semibold text-foreground/85">
                      {person.referred_name ??
                        (de ? "Bewerber:in" : "Applicant")}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function StatTile(props: { value: string; label: string; accent?: boolean }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
      <p
        className={`font-display text-2xl font-extrabold leading-none tracking-tight tabular-nums ${
          props.accent ? "text-turkis-strong" : "text-foreground"
        }`}
      >
        {props.value}
      </p>
      <p className="mt-1 text-[11px] font-semibold leading-tight text-muted-foreground">
        {props.label}
      </p>
    </div>
  );
}

function NavRow(props: {
  count: number;
  title: string;
  subtitle: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      className="flex w-full items-center gap-3.5 rounded-2xl border border-border bg-card p-4 text-left shadow-sm transition hover:bg-turkis-fill-soft"
    >
      <span className="grid h-7 min-w-7 shrink-0 place-items-center rounded-full bg-turkis-fill px-2 text-xs font-bold tabular-nums text-petrol">
        {props.count}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[15px] font-bold tracking-tight">
          {props.title}
        </span>
        <span className="mt-0.5 block text-[12.5px] text-muted-foreground">
          {props.subtitle}
        </span>
      </span>
      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
    </button>
  );
}

/* ══════════════════════════ rooms ══════════════════════════ */

function RoomsScreen(props: {
  de: boolean;
  loading: boolean;
  rooms: ReferrableRoom[];
  /** False while the tenant has no code — sharing a room without one is pointless. */
  canShare: boolean;
  onBack: () => void;
  onShareRoom: (room: ReferrableRoom) => void;
}) {
  const { de, loading, rooms, canShare, onBack, onShareRoom } = props;

  const [city, setCity] = useState<string | null>(null);

  // Cities that actually have a room to refer someone to, busiest first — a
  // chip for a city with one room is worth less than one with thirty, and
  // alphabetical would bury München behind Augsburg.
  const cities = useMemo(() => {
    const counts = new Map<string, number>();
    for (const room of rooms) {
      const name = (room.city ?? "").trim();
      if (name) counts.set(name, (counts.get(name) ?? 0) + 1);
    }
    return [...counts.entries()].sort(
      (a, b) => b[1] - a[1] || a[0].localeCompare(b[0])
    );
  }, [rooms]);

  // A filter the data cannot satisfy is a dead end: if the selected city
  // disappears on a refetch, fall back to showing everything.
  const activeCity = city && cities.some(([c]) => c === city) ? city : null;
  const visible = activeCity
    ? rooms.filter((r) => (r.city ?? "").trim() === activeCity)
    : rooms;

  return (
    <>
      <ScreenHeader
        onBack={onBack}
        title={de ? "Verfügbare Zimmer" : "Available rooms"}
        subtitle={
          de
            ? "Frei jetzt oder mit bestätigtem Auszugsdatum. Tippe auf ein Zimmer für das Inserat, oder teile es – dein Code ist im Link schon drin."
            : "Free now or with a confirmed move-out date. Tap a room for its listing, or share it — your code travels in the link."
        }
      />

      {/* One chip per city, only when there is more than one to choose
          between — a single "All cities" chip is a control that does nothing. */}
      {!loading && cities.length > 1 && (
        <div className="flex flex-col gap-2.5">
          <span className="text-[10.5px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
            {de ? "Stadt" : "City"}
          </span>
          <div className="flex flex-wrap gap-2">
            <CityChip
              active={!activeCity}
              count={rooms.length}
              onClick={() => setCity(null)}
            >
              {de ? "Alle Städte" : "All cities"}
            </CityChip>
            {cities.map(([name, count]) => (
              <CityChip
                key={name}
                active={activeCity === name}
                count={count}
                onClick={() => setCity(name)}
              >
                {name}
              </CityChip>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <PanelLoading />
      ) : rooms.length === 0 ? (
        <EmptyState
          text={
            de
              ? "Gerade sind keine Zimmer frei. Schau bald wieder vorbei – dein Code gilt weiterhin."
              : "No rooms are free right now. Check back soon — your code keeps working."
          }
        />
      ) : (
        <div className="grid gap-3 lg:grid-cols-2">
          {visible.map((room) => (
            <article
              key={room.id}
              className="flex flex-col gap-3 rounded-2xl border border-border bg-card p-4 shadow-sm transition hover:border-turkis-strong"
            >
              <div className="flex items-start gap-3.5">
                <RoomThumb room={room} />

                <div className="flex min-w-0 flex-1 flex-col items-start gap-1.5">
                  {/* The room name IS the link to its public listing — the same
                      page a friend would land on from the marketing site. */}
                  <a
                    href={roomListingUrl(room.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[15px] font-bold leading-snug tracking-tight hover:text-turkis-strong hover:underline"
                  >
                    {room.title}
                    <ExternalLink className="ml-1.5 inline-block h-3 w-3 align-[-1px] opacity-60" />
                  </a>

                  <p className="text-[12.5px] text-muted-foreground">
                    {[room.propertyTitle, room.city]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                  {/* Free today vs. a confirmed move-out date — the friend's
                      planning question, answered before they tap. */}
                  {room.availableFrom ? (
                    <p className="text-[12px] font-semibold text-amber-700 dark:text-amber-300">
                      {de ? "Frei ab " : "Available from "}
                      {formatNextAvailableDate(
                        room.availableFrom,
                        de ? "de" : "en"
                      )}
                    </p>
                  ) : (
                    <p className="text-[12px] font-semibold text-turkis-strong">
                      {de ? "Jetzt frei" : "Available now"}
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-auto flex items-center justify-between gap-3 border-t border-border pt-3">
                <p className="whitespace-nowrap font-display text-[17px] font-extrabold tabular-nums text-petrol dark:text-turkis">
                  {room.price} €
                  <span className="ml-1 text-xs font-medium text-muted-foreground">
                    / {de ? "Monat" : "month"}
                  </span>
                </p>
                <button
                  type="button"
                  onClick={() => onShareRoom(room)}
                  disabled={!canShare}
                  title={
                    canShare
                      ? undefined
                      : de
                        ? "Dein Empfehlungscode ist noch nicht verfügbar."
                        : "Your referral code isn't available yet."
                  }
                  className="inline-flex items-center gap-2 rounded-full bg-petrol px-3.5 py-2 text-[12.5px] font-bold text-white transition hover:bg-petrol-darker disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-petrol"
                >
                  {de ? "Zimmer teilen" : "Share room"}
                  <Share2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
    </>
  );
}

/**
 * One city filter pill. Carries its count because "München 30" and
 * "Augsburg 1" are different propositions, and the number is the cheapest way
 * to say so before the tenant taps.
 */
function CityChip({
  active,
  count,
  onClick,
  children,
}: {
  active: boolean;
  count: number;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-[13px] font-semibold transition ${
        active
          ? "border-petrol bg-petrol text-white"
          : "border-border bg-card text-foreground/80 hover:border-turkis-strong hover:text-petrol"
      }`}
    >
      {children}
      <span
        className={`text-[11px] font-bold tabular-nums ${
          active ? "text-white/60" : "text-muted-foreground"
        }`}
      >
        {count}
      </span>
    </button>
  );
}

/**
 * A room's cover photo, at thumbnail size.
 *
 * Falls back to the brand gradient with the room's initials when there is no
 * photo on file — and does the same if the image 404s, which happens for the
 * older Storyblok-synced rows. A broken-image icon on a listing a tenant is
 * about to recommend looks worse than no photo at all.
 */
function RoomThumb({ room }: { room: ReferrableRoom }) {
  const [failed, setFailed] = useState(false);
  const src = failed ? null : room.photo;

  return (
    <div className="relative h-[72px] w-[72px] shrink-0 overflow-hidden rounded-2xl bg-gradient-petrol sm:h-20 sm:w-20">
      {src ? (
        <img
          src={getImageUrl(src, { width: THUMB_WIDTH }) ?? src}
          srcSet={getImageSrcSet(src, THUMB_SRCSET)}
          sizes="80px"
          alt=""
          loading="lazy"
          decoding="async"
          onError={() => setFailed(true)}
          className="h-full w-full object-cover"
        />
      ) : (
        <>
          <span
            aria-hidden
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(circle at 78% 18%, hsl(var(--turkis) / 0.42), transparent 62%)",
            }}
          />
          <span
            aria-hidden
            className="absolute inset-0 grid place-items-center font-display text-[13px] font-extrabold text-white/90"
          >
            {room.title.slice(0, 4)}
          </span>
        </>
      )}
    </div>
  );
}

/* ══════════════════════════ my referrals ══════════════════════════ */

function MineScreen(props: {
  de: boolean;
  loading: boolean;
  items: ReferralItem[];
  onBack: () => void;
  onShare: () => void;
}) {
  const { de, loading, items, onBack, onShare } = props;

  return (
    <>
      <ScreenHeader
        onBack={onBack}
        title={de ? "Meine Empfehlungen" : "My referrals"}
        subtitle={
          de
            ? "Aktualisiert sich, sobald coliviq eine Bewerbung genehmigt oder einen unterschriebenen Vertrag erfasst."
            : "Updates the moment coliviq approves an application or records a signed contract."
        }
      />

      {loading ? (
        <PanelLoading />
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-border bg-card p-8 text-center shadow-sm">
          <p className="text-sm text-muted-foreground">
            {de
              ? "Noch keine Empfehlungen. Teile deinen Code – sobald sich jemand damit bewirbt, erscheint er hier."
              : "No referrals yet. Share your code — as soon as someone applies with it, they show up here."}
          </p>
          <button
            type="button"
            onClick={onShare}
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-petrol px-5 py-2.5 text-sm font-bold text-white transition hover:bg-petrol-darker"
          >
            {de ? "Jetzt teilen" : "Share now"}
            <Share2 className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <div className="grid gap-3 lg:grid-cols-2">
          {items.map((item) => (
            <ReferralCard key={item.id} item={item} de={de} />
          ))}
        </div>
      )}
    </>
  );
}

function ReferralCard({ item, de }: { item: ReferralItem; de: boolean }) {
  const reached = stageIndex(item.stage);
  const closed = !!item.closed_at && item.stage !== "rewarded";
  const lang = de ? "de" : "en";

  const badge: Record<ReferralStage, string> = {
    applied: "bg-muted text-muted-foreground border-border",
    approved:
      "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900",
    signed: "bg-turkis-fill text-turkis-strong border-turkis-strong",
    rewarded: "bg-turkis text-petrol-darker border-turkis",
  };

  return (
    // min-w-0 + overflow-hidden: as a grid item this card would otherwise take
    // its min-content width, and on a phone the German stage pill plus the
    // four-rung stepper pushed it past the viewport (COL-434, item 6).
    <article className="flex min-w-0 flex-col gap-3.5 overflow-hidden rounded-2xl border border-border bg-card p-4 shadow-sm">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-turkis-fill text-[12.5px] font-bold text-petrol">
          {referralInitials(item.referred_name)}
        </span>
        <div className="min-w-0 flex-1 basis-32">
          <p className="truncate text-[14.5px] font-bold">
            {item.referred_name ?? (de ? "Bewerber:in" : "Applicant")}
          </p>
          <p className="truncate text-xs text-muted-foreground">
            {item.room_label ?? (de ? "Zimmer entfernt" : "Room removed")}
          </p>
        </div>
        {/* Wraps onto its own line when the name and pill cannot share one —
            "VERTRAG UNTERSCHRIEBEN" was being clipped mid-word on iPhones. */}
        <span
          className={`ml-auto max-w-full shrink-0 truncate rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${
            closed
              ? "bg-muted text-muted-foreground border-border"
              : badge[item.stage]
          }`}
        >
          {closed ? (de ? "Beendet" : "Closed") : stageLabel(item.stage, lang)}
        </span>
      </div>

      {/* Four rungs. Each carries its own label — an unlabelled dot rail makes
          the tenant guess what stage three was. */}
      <ol
        className="flex min-w-0 items-start"
        aria-label={stageLabel(item.stage, lang)}
      >
        {REFERRAL_STAGES.map((stage, i) => {
          const n = i + 1;
          const done = n <= reached && !closed;
          const next = n === reached + 1 && !closed;
          return (
            <li
              key={stage}
              className="relative flex min-w-0 flex-1 flex-col items-center gap-1.5"
            >
              {i > 0 && (
                <span
                  aria-hidden
                  className={`absolute left-0 right-1/2 top-2 h-0.5 ${
                    done ? "bg-turkis" : "bg-border"
                  }`}
                />
              )}
              {i < REFERRAL_STAGES.length - 1 && (
                <span
                  aria-hidden
                  className={`absolute left-1/2 right-0 top-2 h-0.5 ${
                    n < reached && !closed ? "bg-turkis" : "bg-border"
                  }`}
                />
              )}
              <span
                className={`relative z-10 grid h-[18px] w-[18px] place-items-center rounded-full border-2 ${
                  done
                    ? "border-turkis bg-turkis text-petrol-darker"
                    : next
                      ? "border-turkis bg-card ring-4 ring-turkis-fill"
                      : "border-border bg-card"
                }`}
              >
                {done && <Check className="h-2.5 w-2.5" strokeWidth={4} />}
              </span>
              <span
                className={`w-full max-w-[76px] break-words px-0.5 text-center text-[9.5px] leading-tight ${
                  done || next
                    ? "font-bold text-foreground"
                    : "font-semibold text-muted-foreground"
                }`}
              >
                {stageLabel(stage, lang)}
              </span>
            </li>
          );
        })}
      </ol>

      <div className="flex items-center justify-between gap-3 border-t border-border pt-3">
        <p className="min-w-0 text-[12.5px] text-muted-foreground">
          {stageHint(item, lang)}
        </p>
        {item.stage === "rewarded" && (
          <p className="shrink-0 text-[13px] font-bold text-turkis-strong">
            −{formatEuroFromCents(item.reward_amount_cents, lang)}
          </p>
        )}
      </div>
    </article>
  );
}

/* ══════════════════════════ share sheet ══════════════════════════ */

function ShareSheet(props: {
  de: boolean;
  code: string;
  message: string;
  onMessage: (v: string) => void;
  roomLabel: string | null;
  onClose: () => void;
}) {
  const { de, code, message, onMessage, roomLabel, onClose } = props;
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const encoded = encodeURIComponent(message);
  const channels = [
    {
      key: "whatsapp",
      label: "WhatsApp",
      icon: MessageCircle,
      href: `https://wa.me/?text=${encoded}`,
    },
    {
      key: "email",
      label: de ? "E-Mail" : "Email",
      icon: Mail,
      href: `mailto:?subject=${encodeURIComponent(
        de ? "Zieh zu coliviq" : "Come live at coliviq"
      )}&body=${encoded}`,
    },
    {
      key: "sms",
      label: "SMS",
      icon: MessageSquare,
      // The `?&body=` form is the one that works on both iOS and Android; plain
      // `?body=` is silently dropped by iOS Messages.
      href: `sms:?&body=${encoded}`,
    },
  ];

  const nativeShare = async () => {
    if (!navigator.share) {
      toast.error(
        de
          ? "Dein Browser unterstützt das Teilen-Menü nicht."
          : "Your browser doesn't support the share menu."
      );
      return;
    }
    try {
      await navigator.share({ text: message });
    } catch {
      // The user dismissing the OS share sheet rejects the promise. That is a
      // decision, not a failure — say nothing.
    }
  };

  const copyMessage = async () => {
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error(de ? "Kopieren nicht möglich." : "Couldn't copy.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
      <button
        type="button"
        aria-label={de ? "Schließen" : "Close"}
        onClick={onClose}
        className="absolute inset-0 bg-petrol-darker/50 backdrop-blur-[2px]"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={de ? "Empfehlung teilen" : "Share referral"}
        className="relative max-h-[88vh] w-full overflow-y-auto rounded-t-3xl bg-card p-5 pb-8 shadow-lg sm:max-w-md sm:rounded-3xl sm:pb-6"
      >
        <div className="mx-auto mb-4 h-1 w-9 rounded-full bg-border sm:hidden" />

        <div className="mb-4 flex items-center justify-between gap-3">
          <h3 className="font-display text-[17px] font-bold tracking-tight">
            {de ? "Empfehlung teilen" : "Share your code"}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label={de ? "Schließen" : "Close"}
            className="grid h-8 w-8 place-items-center rounded-xl border border-border text-muted-foreground transition hover:bg-muted"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {roomLabel && (
          <p className="mb-3.5 flex items-center gap-2 rounded-xl bg-turkis-fill px-3 py-2.5 text-[13px] font-bold text-petrol">
            {roomLabel}
          </p>
        )}

        <p className="mb-2 text-[10.5px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
          {de ? "Dein Code" : "Your code"}
        </p>
        <div className="flex items-center gap-3 rounded-xl border border-border bg-muted px-3.5 py-3">
          <span className="flex-1 text-[15.5px] font-bold tracking-wide tabular-nums">
            {code}
          </span>
          <button
            type="button"
            onClick={copyMessage}
            aria-label={de ? "Nachricht kopieren" : "Copy message"}
            className="grid h-8 w-8 place-items-center rounded-lg border border-turkis-strong bg-turkis-fill text-turkis-strong transition hover:brightness-95"
          >
            {copied ? (
              <Check className="h-4 w-4" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
          </button>
        </div>

        <div className="my-5 grid grid-cols-4 gap-2">
          {channels.map(({ key, label, icon: Icon, href }) => (
            <a
              key={key}
              href={href}
              target={key === "whatsapp" ? "_blank" : undefined}
              rel={key === "whatsapp" ? "noopener noreferrer" : undefined}
              className="flex flex-col items-center gap-2 py-1.5"
            >
              <span className="grid h-11 w-11 place-items-center rounded-full border border-border bg-turkis-fill-soft text-petrol transition hover:border-turkis-strong hover:bg-turkis-fill">
                <Icon className="h-5 w-5" />
              </span>
              <span className="text-[11.5px] font-medium text-foreground/80">
                {label}
              </span>
            </a>
          ))}
          <button
            type="button"
            onClick={nativeShare}
            className="flex flex-col items-center gap-2 py-1.5"
          >
            <span className="grid h-11 w-11 place-items-center rounded-full border border-border bg-turkis-fill-soft text-petrol transition hover:border-turkis-strong hover:bg-turkis-fill">
              <Upload className="h-5 w-5" />
            </span>
            <span className="text-[11.5px] font-medium text-foreground/80">
              {de ? "Mehr" : "More"}
            </span>
          </button>
        </div>

        <label
          htmlFor="referral-share-message"
          className="mb-2 block text-[10.5px] font-bold uppercase tracking-[0.12em] text-muted-foreground"
        >
          {de
            ? "Vorschau-Nachricht (bearbeitbar)"
            : "Preview message (editable)"}
        </label>
        <textarea
          id="referral-share-message"
          value={message}
          onChange={(e) => onMessage(e.target.value)}
          rows={4}
          className="min-h-24 w-full resize-y rounded-xl border border-border bg-muted px-3.5 py-3 text-[13px] leading-relaxed text-foreground outline-none focus:border-turkis-strong focus:ring-2 focus:ring-turkis-fill"
        />
      </div>
    </div>
  );
}

/* ══════════════════════════ shared bits ══════════════════════════ */

function ScreenHeader(props: {
  onBack: () => void;
  title: string;
  subtitle: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <button
        type="button"
        onClick={props.onBack}
        aria-label="Back"
        className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-xl border border-border bg-card text-foreground/70 transition hover:bg-muted"
      >
        <ArrowLeft className="h-4 w-4" />
      </button>
      <div className="min-w-0">
        <h3 className="font-display text-lg font-bold tracking-tight">
          {props.title}
        </h3>
        <p className="mt-0.5 text-[12.5px] text-muted-foreground">
          {props.subtitle}
        </p>
      </div>
    </div>
  );
}

/**
 * Shown when the tenant's own code could not be read.
 *
 * Without it every share control on the page is disabled for no visible reason,
 * which reads as a broken feature rather than a temporary one.
 */
/**
 * The totals below this notice are the EMPTY_REFERRALS fallback, not a fact
 * about this tenant. Says so, rather than letting zeros speak for themselves.
 */
function ReferralsUnavailableNotice({ de }: { de: boolean }) {
  return (
    <div
      role="status"
      className="flex items-start gap-3 rounded-2xl border border-amber-300 bg-amber-50 p-4 text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200"
    >
      <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
      <p className="text-[13px] leading-relaxed">
        {de
          ? "Deine Empfehlungen konnten nicht geladen werden. Die Zahlen unten sind deshalb noch leer – sie sind kein Hinweis darauf, dass Empfehlungen fehlen. Lade die Seite neu; falls es bestehen bleibt, melde dich beim Support."
          : "We couldn't load your referrals, so the figures below are blank — that's this page failing to load, not your referrals going missing. Reload the page; if it keeps happening, contact support."}
      </p>
    </div>
  );
}

function CodeUnavailableNotice({ de }: { de: boolean }) {
  return (
    <div
      role="status"
      className="flex items-start gap-3 rounded-2xl border border-amber-300 bg-amber-50 p-4 text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200"
    >
      <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
      <p className="text-[13px] leading-relaxed">
        {de
          ? "Dein Empfehlungscode konnte nicht geladen werden, deshalb ist das Teilen gerade deaktiviert. Lade die Seite neu – falls es bestehen bleibt, melde dich beim Support."
          : "We couldn't load your referral code, so sharing is disabled for now. Reload the page — if it keeps happening, contact support."}
      </p>
    </div>
  );
}

function PanelLoading() {
  return (
    <div className="flex items-center justify-center py-12 text-muted-foreground">
      <Loader2 className="h-5 w-5 animate-spin" />
    </div>
  );
}

function EmptyState({ text }: { text: string }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-8 text-center shadow-sm">
      <p className="text-sm text-muted-foreground">{text}</p>
    </div>
  );
}
